

import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PullToRefresh } from 'react-js-pull-to-refresh';
import { PullDownContent, ReleaseContent } from 'react-js-pull-to-refresh';
import { RootState } from '../../Redux/Store';
import { AppContext } from '../../Context/AppContext';
import { TMyLoadsTabs } from './MyLoadsContainer.type';
import { fbLogEvent } from '../../Helpers/FirebaseHelper';
import { TSearchTabs } from '../Search/SearchContainer.type';
import Tooltip from '../../Components/Shared/Tooltip/Tooltip';
import { deleteCookie, getCookie } from '../../Utils/CookieUtils';
import { EVENT, SCREEN } from '../../Constants/AnalyticsConstants';
import { useAppDispatch, useAppSelector } from '../../Hooks/Hooks';

import LoadsContainer from '../../Components/LoadsContainer/LoadsContainer';
import { getAllShipmentsRequestPayload } from '../../Utils/APIRequestsPlayloads';
import { updateSearchSectionSelectedTab } from '../../Redux/Reducers/SearchSlice';
import { getLoadCount, getLoads, sendFBLogEvent } from '../../Utils/MyLoads.utils';
import MyLoadsTabsComponent from '../../Components/MyLoadsTabs/MyLoadsTabsComponent';
import { COOKIE_IDS, PULL_TO_REFRESH, TOOLTIP_MESSAGE } from '../../Constants/Constants';
import { getMyLoadsData, updateMyLoadsSectionSelectedTab } from '../../Redux/Reducers/MyLoadsSlice';
import './MyLoadsContainer.scss';

const MyLoadsContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobile } = useContext(AppContext);
  const [showTooltip, setTooltip] = useState<boolean>(false);
  const showBanner = useAppSelector((state: RootState) => state.ContentOrConfig?.showBanner);
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { myLoadsData, myLoadsSectionSelectedTab } = useAppSelector(
    (state: RootState) => state.myLoads,
  );
  const { searchSectionSelectedTab } = useAppSelector(
    (state: RootState) => state?.search,
  );
  const driverId = memberData?.member?.userApplicationDetails?.[0].attributes.DriverId;

  useEffect(() => {
    const loadId = getCookie(COOKIE_IDS.SHIPMENT_ID);
    if (loadId) {
      deleteCookie(COOKIE_IDS.SHIPMENT_ID);
    }
  }, []);

  useEffect(() => {
    if (driverId) {
      myLoadsSectionSelectedTab?.forEach((item: any) => {
        if (item.selected) {
          const requestPayload = getAllShipmentsRequestPayload(driverId, item.slug, 0);
          dispatch(getMyLoadsData({ requestPayload }));
        }
      });
    }
  }, [dispatch, memberData]);

  useEffect(() => {
    dispatch(
      updateMyLoadsSectionSelectedTab(
        myLoadsSectionSelectedTab?.map((item: TMyLoadsTabs) => {
          return { ...item, count: getLoadCount(item.slug, myLoadsData) };
        }),
      ),
    );

    fbLogEvent(EVENT.SV_VIEW_MYLOADS, {
      screen: SCREEN.MYLOADS,
    });
  }, [myLoadsData]);

  const handleTabChange = (slug: string) => {
    const driverId = memberData?.member?.userApplicationDetails?.[0].attributes.DriverId;
    if (driverId) {
      const requestPayload = getAllShipmentsRequestPayload(driverId, slug, 0);
      dispatch(getMyLoadsData({ requestPayload }));
    }

    dispatch(
      updateMyLoadsSectionSelectedTab(
        myLoadsSectionSelectedTab?.map((item: TMyLoadsTabs, index: number) => {
          item?.slug === slug && sendFBLogEvent(slug);
          return { ...item, selected: item?.slug === slug };
        }),
      ),
    );
    if (isMobile) return navigate('/myloads/loads');
  };

  const onRefresh = () => {
    return new Promise((resolve) => {
      const callback = () => {
        resolve(null);
      };
      if (driverId) {
        const requestPayload = getAllShipmentsRequestPayload(driverId, 'DriverAssigned', 0);
        dispatch(getMyLoadsData({ requestPayload, hideLoader: true, callback }));
      }
    });
  };

  return (
    <div
      className={`MyLoads_Container${showBanner ? ' AlertBannerVisible' : ''} ${isMobile ? 'mobile' : 'web'}`}
      data-testid="MyLoadsContainer"
    >
      {!isMobile && (
        <div className="header">
          <div className="Left_Col">My Loads</div>
          <div className="Right_Col">
            <div className="Tooltip_Container">
              <Tooltip message={TOOLTIP_MESSAGE} isActive={showTooltip} clearIndex={true} />
            </div>
            <div>
              {/*TODO: Export Date - hide, will come much later
             <button className="secondary-cta-btn" style={{ backgroundColor: "transparent" }} onClick={() => {
              setTooltip(true);
              setTimeout(() => {
                setTooltip(false);
              }, 2000);

            }}>
              Export Data
            </button> */}
            </div>
            <div style={{ width: 10 }} />
            <button
              className="secondary-cta-btn"
              style={{ backgroundColor: 'transparent' }}
              onClick={() => {
                dispatch(
                  updateSearchSectionSelectedTab(
                    searchSectionSelectedTab?.map((item: TSearchTabs) => {
                      return { ...item, selected: item.slug === 'NewSearch' };
                    }),
                  ),
                );
                navigate('/search', { replace: true });
              }}
            >
              Find New Freight
            </button>
          </div>
        </div>
      )}
      <PullToRefresh
        pullDownContent={<PullDownContent label="" />}
        releaseContent={<ReleaseContent label="" />}
        refreshContent={
          <div>
            <img
              src="/oo/assets/images/Schneider_Spinner-transparent.gif"
              alt=""
              className="PullToRefresh-Loader"
            />
          </div>
        }
        pullDownThreshold={PULL_TO_REFRESH.THRESHOLD}
        onRefresh={onRefresh}
        triggerHeight={PULL_TO_REFRESH.TRIGGER_HEIGHT}
        backgroundColor="#e8e5e1"
        startInvisible={true}
      >
        <MyLoadsTabsComponent tabs={myLoadsSectionSelectedTab} handleTabChange={handleTabChange} />
      </PullToRefresh>
      {!isMobile && (
        <div className={`MyLoads_Section`}>
          {myLoadsSectionSelectedTab?.map((item: any) => {
            if (item.selected) {
              return (
                <LoadsContainer
                  key={item.slug}
                  loads={getLoads(item.slug, myLoadsData)}
                  label=""
                  totalLoadsCount={getLoadCount(item.slug, myLoadsData)}
                  additionalClass="Result_Section"
                  isMyLoads={true}
                />
              );
            }
          })}
        </div>
      )}
    </div>
  );
};

export default MyLoadsContainer;
